// Per-species brand palettes.
//
// The UI primary color swaps when the user selects a species (see
// useSpeciesTheme hook). Each species defines a light and a dark palette
// using Tailwind-style stops (50, 100, 300, 500, 700, 900).
//
// Palettes can also live in the DB (species.brand_color_light /
// brand_color_dark JSONB columns) — if present there, DB values win. The
// constants below are a compile-time fallback so the app still themes
// correctly if the DB row is missing colors.

import type { Species } from './types';
import type { BrandKey } from './branding';
import { DEFAULT_BRAND } from './branding';

export type BrandStop = '50' | '100' | '300' | '500' | '700' | '900';

export type BrandPalette = Record<BrandStop, string>;

export type SpeciesBrandTheme = {
  light: BrandPalette;
  dark: BrandPalette;
};

/**
 * Per-brand palettes for a species. Missing brand keys are resolved by
 * falling back to the DEFAULT_BRAND ("cherry") at runtime.
 */
export type SpeciesBrandedTheme = Partial<Record<BrandKey, SpeciesBrandTheme>>;

/**
 * Fallback palettes keyed by species_id, then by brand. Kept in sync with
 * `supabase/migrations/20260425120000_brand_keyed_species_palettes.sql`.
 *
 * Each species defines a "cherry" palette (always present). Brands without
 * an explicit override fall back to "cherry" at runtime.
 */
export const SPECIES_BRAND_FALLBACK: Record<number, SpeciesBrandedTheme> = {
  // Cherry 🍒
  2: {
    cherry: {
      light: {
        '50':  '#fff0f5',
        '100': '#ffabc5',
        '300': '#ff7f9f',
        '500': '#ff5379',
        '700': '#BE123C',
        '900': '#9f1239',
      },
      dark: {
        '50':  '#2a0b13',
        '100': '#4c1127',
        '300': '#9f1239',
        '500': '#ff5379',
        '700': '#ff2753',
        '900': '#ff7f9f',
      },
    },
    // FruitMarkets — same colors as cherry today; tweak when a custom tone is needed.
    fruitmarkets: {
      light: {
        '50':  '#fff0f5',
        '100': '#ffabc5',
        '300': '#ff7f9f',
        '500': '#ff5379',
        '700': '#BE123C',
        '900': '#9f1239',
      },
      dark: {
        '50':  '#2a0b13',
        '100': '#4c1127',
        '300': '#9f1239',
        '500': '#ff5379',
        '700': '#ff2753',
        '900': '#ff7f9f',
      },
    },
  },
  // Nectarine 🍑 — burnt orange.
  3: {
    cherry: {
      light: {
        '50':  '#fff7ed',
        '100': '#ffedd5',
        '300': '#fdba74',
        '500': '#f97316',
        '700': '#c2410c',
        '900': '#7c2d12',
      },
      dark: {
        '50':  '#1c0f06',
        '100': '#3a1d0b',
        '300': '#9a3412',
        '500': '#f97316',
        '700': '#fb923c',
        '900': '#fdba74',
      },
    },
    fruitmarkets: {
      light: {
        '50':  '#fff7ed',
        '100': '#ffedd5',
        '300': '#fdba74',
        '500': '#f97316',
        '700': '#c2410c',
        '900': '#7c2d12',
      },
      dark: {
        '50':  '#1c0f06',
        '100': '#3a1d0b',
        '300': '#9a3412',
        '500': '#f97316',
        '700': '#fb923c',
        '900': '#fdba74',
      },
    },
  },
  // Sugar Plum 🟪 — purple.
  4: {
    cherry: {
      light: {
        '50':  '#faf5ff',
        '100': '#f3e8ff',
        '300': '#d8b4fe',
        '500': '#a855f7',
        '700': '#7e22ce',
        '900': '#581c87',
      },
      dark: {
        '50':  '#1a0b24',
        '100': '#2e1065',
        '300': '#6b21a8',
        '500': '#a855f7',
        '700': '#c084fc',
        '900': '#d8b4fe',
      },
    },
    fruitmarkets: {
      light: {
        '50':  '#faf5ff',
        '100': '#f3e8ff',
        '300': '#d8b4fe',
        '500': '#a855f7',
        '700': '#7e22ce',
        '900': '#581c87',
      },
      dark: {
        '50':  '#1a0b24',
        '100': '#2e1065',
        '300': '#6b21a8',
        '500': '#a855f7',
        '700': '#c084fc',
        '900': '#d8b4fe',
      },
    },
  },
  // Kiwi 🥝 — green.
  8: {
    cherry: {
      light: {
        '50':  '#f0fdf4',
        '100': '#dcfce7',
        '300': '#86efac',
        '500': '#22c55e',
        '700': '#15803d',
        '900': '#14532d',
      },
      dark: {
        '50':  '#052e16',
        '100': '#14532d',
        '300': '#166534',
        '500': '#22c55e',
        '700': '#4ade80',
        '900': '#86efac',
      },
    },
    fruitmarkets: {
      light: {
        '50':  '#f0fdf4',
        '100': '#dcfce7',
        '300': '#86efac',
        '500': '#22c55e',
        '700': '#15803d',
        '900': '#14532d',
      },
      dark: {
        '50':  '#052e16',
        '100': '#14532d',
        '300': '#166534',
        '500': '#22c55e',
        '700': '#4ade80',
        '900': '#86efac',
      },
    },
  },
};

/** Default theme (cherry species, cherry brand). Used when no species is selected. */
export const DEFAULT_BRAND_THEME: SpeciesBrandTheme =
  SPECIES_BRAND_FALLBACK[2].cherry as SpeciesBrandTheme;

/**
 * Resolve the palette for a given species + mode + brand.
 *
 * Resolution order:
 *   1. DB column `species.brand_color_*` keyed by `brand`
 *   2. DB column `species.brand_color_*` keyed by DEFAULT_BRAND ("cherry")
 *   3. In-code fallback `SPECIES_BRAND_FALLBACK[id]` keyed by `brand`
 *   4. In-code fallback keyed by DEFAULT_BRAND
 *   5. DEFAULT_BRAND_THEME (cherry-cherry)
 *
 * `brand` defaults to DEFAULT_BRAND so existing callers keep working.
 */
export function resolveBrandPalette(
  species: Species | null | undefined,
  mode: 'light' | 'dark',
  brand: BrandKey = DEFAULT_BRAND
): BrandPalette {
  if (!species) return DEFAULT_BRAND_THEME[mode];

  const dbBranded =
    mode === 'light' ? species.brand_color_light : species.brand_color_dark;
  const dbForBrand = pickBrandedPalette(dbBranded, brand);
  if (isValidPalette(dbForBrand)) return dbForBrand;

  const codeFallback = SPECIES_BRAND_FALLBACK[species.id];
  if (codeFallback) {
    const themed = codeFallback[brand] ?? codeFallback[DEFAULT_BRAND];
    if (themed) return themed[mode];
  }

  return DEFAULT_BRAND_THEME[mode];
}

/**
 * Read a brand-keyed JSONB palette and return the sub-palette for the
 * requested brand, falling back to DEFAULT_BRAND. Returns null if the
 * value isn't shaped like a brand-keyed object.
 */
function pickBrandedPalette(
  value: unknown,
  brand: BrandKey
): BrandPalette | null {
  if (!value || typeof value !== 'object') return null;
  const obj = value as Record<string, unknown>;
  // Brand-keyed shape: at least one known brand key with a palette inside.
  const candidate = (obj[brand] ?? obj[DEFAULT_BRAND]) as unknown;
  if (isValidPalette(candidate)) return candidate;
  return null;
}

/** Type guard: ensures the JSONB blob has all required stops as strings. */
function isValidPalette(value: unknown): value is BrandPalette {
  if (!value || typeof value !== 'object') return false;
  const required: BrandStop[] = ['50', '100', '300', '500', '700', '900'];
  return required.every(
    (stop) =>
      typeof (value as Record<string, unknown>)[stop] === 'string' &&
      ((value as Record<string, string>)[stop] as string).length > 0
  );
}

const BRAND_STOPS: BrandStop[] = ['50', '100', '300', '500', '700', '900'];

/** Parse `#rrggbb` or `#rgb` into a `[r, g, b]` tuple (0-255). Null on invalid input. */
function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([a-f\d]{6}|[a-f\d]{3})$/i.exec(hex.trim());
  if (!match) return null;
  let full = match[1];
  if (full.length === 3) {
    full = full
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** Convert `#rrggbb` to `r g b` triplet (Tailwind space-separated form). */
function hexToRgbTriplet(hex: string): string | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  return `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
}

/**
 * Convert `#rrggbb` to `h s% l%` triplet (NextUI space-separated HSL form).
 * NextUI's theme plugin emits `--nextui-primary-*` in this format and
 * consumes them via `hsl(var(--nextui-primary-*) / <alpha-value>)`.
 */
function hexToHslTriplet(hex: string): string | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
}

/**
 * Map our 6-stop brand palette to NextUI's 10-stop primary palette.
 * NextUI primary is generated from the Tailwind config, so this must
 * match the stop→stop mapping declared there (see tailwind.config.js).
 */
const NEXTUI_STOP_TO_BRAND: Record<string, BrandStop> = {
  '50': '50',
  '100': '100',
  '200': '100',
  '300': '300',
  '400': '500',
  '500': '500',
  '600': '700',
  '700': '700',
  '800': '900',
  '900': '900',
};

/**
 * Inject a palette as CSS custom properties on the root element.
 *
 * Defines two forms per stop so Tailwind's `<alpha-value>` works and
 * plain hex-consuming code (ApexCharts, inline styles) still works:
 *   --brand-500      → "#ff5379"
 *   --brand-500-rgb  → "255 83 121"
 */
export function applyBrandPaletteToRoot(
  palette: BrandPalette,
  root: HTMLElement = document.documentElement
): void {
  BRAND_STOPS.forEach((stop) => {
    const hex = palette[stop];
    root.style.setProperty(`--brand-${stop}`, hex);
    const rgb = hexToRgbTriplet(hex);
    if (rgb) {
      root.style.setProperty(`--brand-${stop}-rgb`, rgb);
    }
  });
  // Convenience alias: --brand-primary is whatever 700 resolves to.
  root.style.setProperty('--brand-primary', palette['700']);
  const primaryRgb = hexToRgbTriplet(palette['700']);
  if (primaryRgb) {
    root.style.setProperty('--brand-primary-rgb', primaryRgb);
  }

  // Override NextUI's generated --nextui-primary-* HSL vars so components
  // using `color='primary'` also follow the species theme.
  Object.entries(NEXTUI_STOP_TO_BRAND).forEach(([nextuiStop, brandStop]) => {
    const hsl = hexToHslTriplet(palette[brandStop]);
    if (hsl) {
      root.style.setProperty(`--nextui-primary-${nextuiStop}`, hsl);
    }
  });
  const defaultHsl = hexToHslTriplet(palette['700']);
  if (defaultHsl) {
    // NextUI's "DEFAULT" shade is exposed as --nextui-primary (no suffix).
    root.style.setProperty('--nextui-primary', defaultHsl);
  }
}

/**
 * Read a brand CSS variable at runtime. Use this for ApexCharts and
 * anywhere else that consumes plain hex strings (they can't read CSS
 * variables directly from SVG / canvas / inline JS series configs).
 *
 * Safe to call during SSR — returns fallback if `document` is undefined.
 */
export function readBrandVar(
  stop: BrandStop | 'primary',
  fallback: string = DEFAULT_BRAND_THEME.light['700']
): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--brand-${stop}`)
    .trim();
  return value || fallback;
}
